// 陀螺仪倾斜（spec §6）：立体书 ±15°、平面地图 ±6°，带平滑。
// iOS 13+ 需要在用户手势里调用 DeviceOrientationEvent.requestPermission()；
// Android / 桌面不需要。拒绝、不支持或一直收不到数据时改用手指拖动倾斜。

export const MAX_TILT_DEG = 15;
/** 平滑时间常数（ms）：约 3τ 后到达目标的 95% */
const SMOOTH_MS = 110;
/** 手指拖动：每多少 CSS px 对应 1° */
const DRAG_PX_PER_DEG = 7;
/** 激活后这么久仍没收到有效方向数据 → 判定为不支持，改用拖动 */
const NO_DATA_MS = 900;

export type GyroPermission = 'unknown' | 'granted' | 'denied' | 'not-needed' | 'unsupported';
export type TiltMode = 'gyro' | 'drag';

type IOSOrientationEvent = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<'granted' | 'denied'> };

const clamp = (v: number, lim: number) => Math.max(-lim, Math.min(lim, v));

export class Gyro {
  /** 平滑后的倾斜角（度）。x = 绕水平轴（前后俯仰，来自 beta），y = 绕竖直轴（左右，来自 gamma） */
  readonly tilt = { x: 0, y: 0 };
  permission: GyroPermission = 'unknown';
  /** 存档用：App 是否申请过、结果如何 */
  asked = false;
  /** 设置页的陀螺仪开关：关掉后不申请权限、不采用方向数据，一律手指拖动倾斜 */
  enabled = true;
  /** 申请结果回调（写入存档） */
  onPermission: ((asked: boolean, granted: boolean | null) => void) | null = null;
  private active = false;
  private maxDeg = MAX_TILT_DEG;
  private pending = false;
  private target = { x: 0, y: 0 };
  private neutral: { beta: number; gamma: number } | null = null;
  private lastDataAt = -1;
  private activeFor = 0;
  private dragging: { x: number; y: number; baseX: number; baseY: number } | null = null;

  constructor() {
    if (typeof window === 'undefined' || typeof DeviceOrientationEvent === 'undefined') {
      this.permission = 'unsupported';
      return;
    }
    const needsAsk = typeof (DeviceOrientationEvent as IOSOrientationEvent).requestPermission === 'function';
    this.permission = needsAsk ? 'unknown' : 'not-needed';
    // 监听始终挂着（便宜），但只有 active 时才采用数据
    window.addEventListener('deviceorientation', (e) => this.onOrientation(e.beta, e.gamma));
  }

  /**
   * 在用户手势（click / pointerup）里调用；iOS 只会弹一次系统框。
   * 第一次调用如果因为"不在手势里"失败，下次手势会再试。
   */
  requestFromGesture(): void {
    if (!this.enabled || this.permission !== 'unknown' || this.pending) return;
    const ios = DeviceOrientationEvent as IOSOrientationEvent;
    if (typeof ios.requestPermission !== 'function') return;
    this.asked = true;
    this.pending = true;
    ios.requestPermission().then(
      (r) => {
        this.pending = false;
        this.permission = r === 'granted' ? 'granted' : 'denied';
        this.onPermission?.(true, r === 'granted');
      },
      () => {
        this.pending = false;
        // 不在用户手势里会 reject；保持 unknown，下次手势再试
        this.asked = false;
      },
    );
  }

  /** 'gyro'：正在收到方向数据；否则 'drag'（拒绝 / 不支持 / 无数据） */
  get mode(): TiltMode {
    if (!this.enabled) return 'drag';
    if (this.permission === 'denied' || this.permission === 'unsupported') return 'drag';
    if (this.lastDataAt < 0) return this.activeFor < NO_DATA_MS ? 'gyro' : 'drag';
    return 'gyro';
  }

  /** maxDeg：立体书 ±15°，终章前的平面地图 ±6°（spec §6） */
  setActive(on: boolean, maxDeg = MAX_TILT_DEG): void {
    if (this.active === on && this.maxDeg === maxDeg) return;
    this.active = on;
    this.maxDeg = maxDeg;
    this.neutral = null; // 进入立体书时的手机姿态 = 正中
    this.lastDataAt = -1;
    this.activeFor = 0;
    this.dragging = null;
    this.target = { x: 0, y: 0 };
    if (!on) this.tilt.x = this.tilt.y = 0;
  }

  get isActive(): boolean {
    return this.active;
  }

  setEnabled(on: boolean): void {
    if (this.enabled === on) return;
    this.enabled = on;
    // 切换后从正中重新开始（不保留另一种模式的目标角）
    this.neutral = null;
    this.lastDataAt = -1;
    this.dragging = null;
    this.target = { x: 0, y: 0 };
  }

  /** 测试钩子：注入一次相对正中的倾斜（度），走与真实事件相同的路径 */
  inject(beta: number, gamma: number): void {
    if (!this.neutral) this.neutral = { beta: 0, gamma: 0 };
    this.onOrientation(this.neutral.beta + beta, this.neutral.gamma + gamma);
  }

  private onOrientation(beta: number | null, gamma: number | null): void {
    if (!this.active || !this.enabled || beta == null || gamma == null) return;
    if (this.dragging) return; // 不会同时发生：拖动只在 drag 模式下
    if (!this.neutral) this.neutral = { beta, gamma };
    this.lastDataAt = this.activeFor;
    // 手机上沿向远离自己倾（beta 变小）→ 画面上沿远离眼睛（绕 x 轴负转）
    this.target.x = clamp(beta - this.neutral.beta, this.maxDeg);
    this.target.y = clamp(gamma - this.neutral.gamma, this.maxDeg);
  }

  // ── 手指拖动倾斜（drag 模式） ──
  dragStart(x: number, y: number): void {
    if (!this.active || this.mode !== 'drag') return;
    this.dragging = { x, y, baseX: this.target.x, baseY: this.target.y };
  }

  dragMove(x: number, y: number): void {
    const d = this.dragging;
    if (!d) return;
    // 手指往下拖 = 把画面下沿按下去 = 上沿朝向眼睛（绕 x 正转）
    this.target.x = clamp(d.baseX + (y - d.y) / DRAG_PX_PER_DEG, this.maxDeg);
    this.target.y = clamp(d.baseY + (x - d.x) / DRAG_PX_PER_DEG, this.maxDeg);
  }

  dragEnd(): void {
    this.dragging = null;
  }

  update(dtMs: number): void {
    if (!this.active) return;
    this.activeFor += dtMs;
    // 拖动模式松手后缓慢回正
    if (this.mode === 'drag' && !this.dragging) {
      const back = 1 - Math.exp(-dtMs / 900);
      this.target.x -= this.target.x * back;
      this.target.y -= this.target.y * back;
    }
    const k = 1 - Math.exp(-dtMs / SMOOTH_MS);
    this.tilt.x += (this.target.x - this.tilt.x) * k;
    this.tilt.y += (this.target.y - this.tilt.y) * k;
  }
}
